import Button from '@codegouvfr/react-dsfr/Button';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import { createModal } from '@codegouvfr/react-dsfr/Modal';
import clsx from 'clsx';
import {
  areFieldsLocked,
  type ProgrammingPlanEditionPhase
} from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlanEditionPhase';
import type { ProgrammingSubPlanFieldSetting } from 'maestro-shared/schema/SpecificData/FieldConfigInput';
import { useContext } from 'react';
import { ApiClientContext } from 'src/services/apiClient';
import { assert, type Equals } from 'tsafe';
import { AddFieldToProgrammingSubPlanModal } from './AddFieldToProgrammingSubPlanModal';
import { ProgrammingSubPlanFieldList } from './ProgrammingSubPlanFieldList';

const addFieldModal = createModal({
  id: 'sampler-form-add-field-modal',
  isOpenedByDefault: false
});

type Props = {
  parentPlanId: string | undefined;
  fields: ProgrammingSubPlanFieldSetting[];
  errorMessage: string | undefined;
  editionPhase: ProgrammingPlanEditionPhase;
  onChange: (fields: ProgrammingSubPlanFieldSetting[]) => void;
};

export const ProgrammingPlanSamplerFormSettings = ({
  parentPlanId,
  fields,
  errorMessage,
  editionPhase,
  onChange,
  ..._rest
}: Props) => {
  assert<Equals<keyof typeof _rest, never>>();

  const locked = areFieldsLocked(editionPhase);

  const apiClient = useContext(ApiClientContext);

  const { data: allFields = [] } = apiClient.useFindAllFieldConfigsQuery();
  const { data: parentPlanSettings } =
    apiClient.useFindProgrammingPlanSettingsQuery(
      { programmingPlanId: parentPlanId as string },
      { skip: !parentPlanId }
    );

  const availableFields = allFields.filter(
    (globalField) =>
      !fields.some(
        ({ fieldId, inheritance }) =>
          fieldId === globalField.id && inheritance !== 'Excluded'
      )
  );

  return (
    <>
      <div
        className={clsx('d-flex-row', 'd-flex-align-center', cx('fr-mb-2w'))}
      >
        <h5 className={cx('fr-mb-0')}>Champs actifs</h5>
        <Button
          className={cx('fr-ml-auto')}
          iconId="fr-icon-add-line"
          onClick={() => addFieldModal.open()}
          disabled={locked || availableFields.length === 0}
        >
          Ajouter un descripteur
        </Button>
      </div>

      {errorMessage && (
        <p className={cx('fr-error-text', 'fr-mb-2w')}>{errorMessage}</p>
      )}

      <ProgrammingSubPlanFieldList
        fields={fields}
        locked={locked}
        onChange={onChange}
        allFields={allFields}
        planFields={parentPlanSettings?.fields ?? []}
      />

      <AddFieldToProgrammingSubPlanModal
        modal={addFieldModal}
        availableFields={availableFields}
        onAdd={(field) =>
          onChange(
            fields.some(({ fieldId }) => fieldId === field.fieldId)
              ? fields.map((f) =>
                  f.fieldId === field.fieldId
                    ? { ...f, inheritance: 'Inherited' as const }
                    : f
                )
              : [...fields, field]
          )
        }
      />
    </>
  );
};
